import { useState, useEffect } from 'react';
import { apiGet, confirmBillingPlan, getPlanInfo } from '../api/client';
import type { Invoice, PlanInfo, ShopifyOrder } from '../types';
import { t } from '../i18n';
import { canManagePlan, openPlanManagement, planManagementButtonLabel } from '../utils/billing';
import { formatDate } from '../utils/date';
import { getEffectiveInvoiceStatus } from '../utils/invoiceStatus';
import { formatPlanLabel } from '../utils/labels';
import { invoiceOrderLabel } from '../utils/order';

interface DashboardProps {
  onError: (error: string | null) => void;
}

export function Dashboard({ onError }: DashboardProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();

    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        loadDashboardData();
      }
    };
    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, []);

  async function loadDashboardData() {
    try {
      const confirmedPlan = await confirmReturnedBillingPlan();
      const [invoiceData, orderData, plan] = await Promise.all([
        apiGet<Invoice[]>('/invoices'),
        apiGet<ShopifyOrder[]>('/orders?limit=25'),
        confirmedPlan ? Promise.resolve(confirmedPlan) : getPlanInfo(),
      ]);
      setInvoices(invoiceData || []);
      setOrders(orderData || []);
      setPlanInfo(plan);
      onError(null);
    } catch {
      onError(t('dashboard_load_error'));
    } finally {
      setLoading(false);
    }
  }

  const total = invoices.length;
  const draft = invoices.filter(i => getEffectiveInvoiceStatus(i) === 'draft').length;
  const issued = invoices.filter(i => getEffectiveInvoiceStatus(i) === 'issued').length;
  const failed = invoices.filter(i => i.validation_status === 'failed').length;
  const orderTotal = orders.length;
  const orderPaid = orders.filter(o => o.financial_status === 'paid').length;
  const orderOpen = orders.filter(o => ['pending', 'authorized', 'partially_paid'].includes(o.financial_status)).length;
  const orderUnfulfilled = orders.filter(o => !o.fulfillment_status || o.fulfillment_status === 'unfulfilled').length;
  const recent = invoices.slice(0, 5);

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="page-container">
      <div className="section-header">
        <div>
          <h1 className="section-title">{t('dashboard_title')}</h1>
        </div>
        <button onClick={loadDashboardData} className="btn btn--secondary">{t('refresh')}</button>
      </div>

      {planInfo && <PlanUsageCard planInfo={planInfo} />}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">{t('invoices_total')}</div>
          <div className="stat-value">{total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('draft')}</div>
          <div className="stat-value" style={{ color: 'var(--color-warning)' }}>{draft}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('issued')}</div>
          <div className="stat-value" style={{ color: 'var(--color-success)' }}>{issued}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('validation_failed')}</div>
          <div className="stat-value" style={{ color: 'var(--color-critical)' }}>{failed}</div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">{t('orders_total')}</div>
          <div className="stat-value">{orderTotal}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('paid')}</div>
          <div className="stat-value" style={{ color: 'var(--color-success)' }}>{orderPaid}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('open')}</div>
          <div className="stat-value" style={{ color: 'var(--color-warning)' }}>{orderOpen}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('unfulfilled')}</div>
          <div className="stat-value" style={{ color: 'var(--color-critical)' }}>{orderUnfulfilled}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{t('latest_invoices')}</h2>
        </div>
        {recent.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">{t('no_invoices_yet')}</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>{t('col_invoice')}</th>
                  <th>{t('col_order')}</th>
                  <th>{t('col_customer')}</th>
                  <th>{t('col_date')}</th>
                  <th>{t('col_total')}</th>
                  <th>{t('col_status')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((inv) => (
                  <tr key={inv.id}>
                    <td className="fw-medium">{inv.invoice_number}</td>
                    <td>{invoiceOrderLabel(inv)}</td>
                    <td>{inv.customer?.company_name || [inv.customer?.first_name, inv.customer?.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className="text-secondary">{formatDate(inv.issue_date)}</td>
                    <td className="fw-medium">{inv.total} {inv.currency}</td>
                    <td><StatusBadge status={getEffectiveInvoiceStatus(inv)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

async function confirmReturnedBillingPlan(): Promise<PlanInfo | null> {
  const params = new URLSearchParams(window.location.search);
  const planHandle = params.get('plan_handle');

  if (!planHandle) {
    return null;
  }

  try {
    const plan = await confirmBillingPlan(planHandle, params.get('charge_id'));
    return plan;
  } finally {
    params.delete('plan_handle');
    params.delete('charge_id');
    const query = params.toString();
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', cleanUrl);
  }
}

function PlanUsageCard({ planInfo }: { planInfo: PlanInfo }) {
  const usagePercent = planInfo.document_limit > 0
    ? Math.round((planInfo.documents_used / planInfo.document_limit) * 100)
    : 0;
  const isNearLimit = usagePercent >= 80;
  const isAtLimit = planInfo.documents_remaining === 0;

  const progressClass = isAtLimit ? 'crit' : isNearLimit ? 'warn' : 'ok';
  const planBadgeClass = planInfo.handle === 'professional' ? 'success' : planInfo.handle === 'starter' ? 'info' : 'neutral';

  return (
    <div className="card mb-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-sm text-secondary mb-2">{t('current_plan')}</div>
          <span className={`badge badge--${planBadgeClass}`} style={{ fontSize: 'var(--font-size-md)', padding: '4px 12px' }}>
            {formatPlanLabel(planInfo.label)}
          </span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="text-sm text-secondary">{t('billing_period')}: {planInfo.billing_period}</div>
          <div className="text-disabled mt-2" style={{ fontSize: 'var(--font-size-xs)' }}>{t('reset_on')} {formatDate(planInfo.reset_date)}</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between mb-2">
          <span className="text-sm">{t('document_usage')}</span>
          <span className="text-sm fw-medium" style={{ color: isAtLimit ? 'var(--color-critical)' : 'var(--color-text-secondary)' }}>
            {planInfo.documents_used} / {planInfo.document_limit}
          </span>
        </div>
        <div className={`progress-bar`}>
          <div className={`progress-bar-fill progress-bar-fill--${progressClass}`} style={{ width: `${Math.min(usagePercent, 100)}%` }} />
        </div>
        <div className="text-disabled mt-2" style={{ fontSize: 'var(--font-size-xs)' }}>
          {planInfo.documents_remaining} {t('remaining_in_period')}
        </div>
      </div>

      {isAtLimit && (
        <div className="banner banner--critical mb-4" style={{ padding: '8px 12px', fontSize: 'var(--font-size-sm)' }}>
          {t('monthly_limit_reached')}
        </div>
      )}

      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-secondary">{planInfo.documents_used} / {planInfo.document_limit} {t('documents_used_label')}</span>
        <button
          type="button"
          onClick={() => openPlanManagement(planInfo)}
          disabled={!canManagePlan(planInfo)}
          title={!canManagePlan(planInfo) ? t('manage_plan_tooltip') : undefined}
          className="btn btn--primary btn--sm"
        >
          {planManagementButtonLabel()}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const badgeClass: Record<string, string> = {
    issued: 'success',
    draft: 'warning',
    cancelled: 'critical',
  };
  const labels: Record<string, string> = {
    issued: t('status_issued'),
    draft: t('status_draft'),
    cancelled: t('status_cancelled'),
  };
  return (
    <span className={`badge badge--${badgeClass[status] || 'neutral'}`}>
      {labels[status] || status}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="page-container">
      <div className="skeleton" style={{ height: 32, width: 150, marginBottom: 'var(--space-6)' }} />
      <div className="stats-grid">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="stat-card">
            <div className="skeleton" style={{ height: 14, width: 80, marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 28, width: 40 }} />
          </div>
        ))}
      </div>
      <div className="skeleton" style={{ height: 200 }} />
    </div>
  );
}
